/* ==========================================================================
   ABOUT PAGE TOOLKIT — assets/about.js
   --------------------------------------------------------------------------
   Vanilla ES6. No dependencies. Loaded with `defer` by any About section.
   The singleton guard means several sections can reference the same file: the
   browser fetches it once, and this IIFE only ever initialises once.

   Public API — later sections plug in without touching the engine:

     AboutTheme.register('accordion', {
       init(root)         { ... },  // on load + shopify:section:load
       destroy(root)      { ... },  // on shopify:section:unload
       onBlockSelect(e)   { ... },  // optional, Theme Editor
       onBlockDeselect(e) { ... }   // optional, Theme Editor
     });

   Modules shipped here:
     • animations — IntersectionObserver reveals for [data-about-animate]
     • logoSlider — marquee / manual carousel for [data-about-logos]
   ========================================================================== */

(function () {
  'use strict';

  /* A duplicated <script> tag must not re-run this. */
  if (window.AboutTheme) return;

  /* CSS keeps content visible while JS is absent (`.no-js [data-about-animate]`). */
  document.documentElement.classList.remove('no-js');

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Animation engine
     Observers are cached by scroll-offset, so N sections sharing the same
     "Trigger offset" setting share a single IntersectionObserver.
  ------------------------------------------------------------------------ */
  class AnimationEngine {
    constructor() {
      this.selector = '[data-about-animate]';
      this.observers = new Map();
    }

    init(root) {
      const scope = root || document;
      const elements = scope.querySelectorAll(this.selector);
      if (!elements.length) return;

      // Reduced motion or legacy browser: reveal everything immediately.
      if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) {
        elements.forEach((el) => el.classList.add('is-visible'));
        return;
      }

      elements.forEach((el) => {
        if (el.dataset.aboutObserved === 'true') return;
        el.dataset.aboutObserved = 'true';

        // Cascading delay: data-about-stagger="2" -> 2 x --as-anim-stagger.
        if (el.hasAttribute('data-about-stagger')) {
          el.style.setProperty('--about-stagger', el.getAttribute('data-about-stagger') || '0');
        }

        this.observerFor(this.offsetFor(el)).observe(el);
      });
    }

    offsetFor(el) {
      const section = el.closest('[data-about-section]');
      return section ? parseInt(section.dataset.animOffset, 10) || 0 : 0;
    }

    repeats(el) {
      const section = el.closest('[data-about-section]');
      return !!section && section.dataset.animOnce === 'false';
    }

    observerFor(offset) {
      const key = String(offset);
      if (this.observers.has(key)) return this.observers.get(key);

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            const el = entry.target;
            if (entry.isIntersecting) {
              el.classList.add('is-visible');
              if (!this.repeats(el)) obs.unobserve(el);
            } else if (this.repeats(el)) {
              el.classList.remove('is-visible');
            }
          });
        },
        { rootMargin: '0px 0px -' + offset + 'px 0px', threshold: 0.01 }
      );

      this.observers.set(key, observer);
      return observer;
    }

    destroy(root) {
      const scope = root || document;
      scope.querySelectorAll(this.selector).forEach((el) => {
        this.observers.forEach((obs) => obs.unobserve(el));
        delete el.dataset.aboutObserved;
      });
    }
  }

  /* ------------------------------------------------------------------------
     Logo slider
     ------------------------------------------------------------------------
     Three modes, chosen at runtime:

       loop   — autoplay + infinite. The group is cloned once and the track is
                translated by exactly one group + one gap, so the seam is
                invisible. Track width = 2S + G, and translateX(-50%) only
                reaches -(S + G/2) — half a gap short. Hence the keyframe uses
                calc(-50% - var(--al-gap) / 2) to land on -(S + G).

       alt    — autoplay without infinite. Single group, ping-pongs between 0
                and -(trackWidth - viewportWidth) via animation-direction:
                alternate. Skipped when the logos already fit.

       manual — autoplay off, reduced motion, or nothing to scroll. The
                viewport is a native scroll container (its default state, so
                it works with no JS at all) plus prev/next buttons that are
                revealed by removing the server-rendered `hidden` attribute.

     Duration is derived from the merchant's speed setting in px/second, so the
     logos travel at a constant pace no matter how many blocks exist.
  ------------------------------------------------------------------------ */
  class LogoSlider {
    constructor() {
      this.selector = '[data-about-logos]';
      this.instances = new Map();
      this.onResize = this.onResize.bind(this);

      this.resizeObserver =
        'ResizeObserver' in window ? new ResizeObserver(this.onResize) : null;

      if (!this.resizeObserver) window.addEventListener('resize', this.onResize, { passive: true });

      // Re-evaluate if the user flips the OS reduced-motion switch mid-session.
      const relayout = () => this.instances.forEach((_, el) => this.layout(el));
      if (REDUCED_MOTION.addEventListener) REDUCED_MOTION.addEventListener('change', relayout);
    }

    init(root) {
      const scope = root || document;
      scope.querySelectorAll(this.selector).forEach((el) => this.setup(el));
    }

    setup(viewport) {
      if (this.instances.has(viewport)) return;

      const track = viewport.querySelector('[data-logos-track]');
      const group = viewport.querySelector('[data-logos-group]');
      if (!track || !group) return;

      const state = {
        track: track,
        group: group,
        clone: null,
        nav: viewport.parentElement.querySelector('[data-logos-nav]'),
        autoplay: viewport.dataset.autoplay === 'true',
        infinite: viewport.dataset.infinite === 'true',
        pauseOnHover: viewport.dataset.pauseHover === 'true',
        speed: Math.max(1, parseFloat(viewport.dataset.speed) || 40)
      };

      this.instances.set(viewport, state);

      if (state.nav) this.wireNav(viewport, state);
      if (this.resizeObserver) this.resizeObserver.observe(viewport);

      this.layout(viewport);
    }

    /** Decides the mode and writes the derived CSS variables. */
    layout(viewport) {
      const state = this.instances.get(viewport);
      if (!state) return;

      const wantsMotion = state.autoplay && !REDUCED_MOTION.matches;

      viewport.classList.remove(
        'about-logos__viewport--loop',
        'about-logos__viewport--alt',
        'about-logos__viewport--pause',
        'is-animated'
      );

      if (!wantsMotion) return this.toManual(viewport, state);

      if (state.infinite) {
        this.ensureClone(state);
        const gap = this.gapOf(state.track);
        const distance = state.group.getBoundingClientRect().width + gap;
        if (distance <= 0) return this.toManual(viewport, state);

        viewport.style.setProperty('--al-duration', distance / state.speed + 's');
        viewport.classList.add('about-logos__viewport--loop', 'is-animated');
      } else {
        this.removeClone(state);
        const shift = state.track.scrollWidth - viewport.clientWidth;
        // Nothing overflows -> nothing to animate, and no scrolling either.
        if (shift <= 1) return this.toManual(viewport, state, true);

        viewport.style.setProperty('--al-shift', shift + 'px');
        viewport.style.setProperty('--al-duration', shift / state.speed + 's');
        viewport.classList.add('about-logos__viewport--alt', 'is-animated');
      }

      if (state.pauseOnHover) viewport.classList.add('about-logos__viewport--pause');

      // An animated viewport can't be scrolled, so it must not be a tab stop.
      viewport.removeAttribute('tabindex');
      if (state.nav) state.nav.hidden = true;
    }

    toManual(viewport, state, nothingToScroll) {
      this.removeClone(state);
      viewport.style.removeProperty('--al-duration');
      viewport.style.removeProperty('--al-shift');

      // A scrollable region must be keyboard reachable (WCAG 2.1.1).
      const scrollable = !nothingToScroll && state.track.scrollWidth > viewport.clientWidth + 1;
      if (scrollable) {
        viewport.setAttribute('tabindex', '0');
      } else {
        viewport.removeAttribute('tabindex');
      }

      if (state.nav) {
        state.nav.hidden = !scrollable;
        if (scrollable) this.updateNav(viewport, state);
      }
    }

    /* -------------------------------------------------------------- clones */

    ensureClone(state) {
      if (state.clone) return;

      const clone = state.group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-logos-group');

      clone.querySelectorAll('[data-about-animate]').forEach((el) => {
        // Clones are never observed, so strip the attribute that hides them.
        el.removeAttribute('data-about-animate');
        el.removeAttribute('data-about-stagger');
        el.removeAttribute('data-about-observed');
      });

      // Duplicate links must not become tab stops.
      clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));

      // Theme Editor hooks would fire twice on a duplicated block.
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
        el.removeAttribute('data-shopify-editor-block');
      });

      state.track.appendChild(clone);
      state.clone = clone;
    }

    removeClone(state) {
      if (!state.clone) return;
      state.clone.remove();
      state.clone = null;
    }

    /* ----------------------------------------------------------------- nav */

    wireNav(viewport, state) {
      const prev = state.nav.querySelector('[data-logos-prev]');
      const next = state.nav.querySelector('[data-logos-next]');
      const step = () => Math.max(120, viewport.clientWidth * 0.85);

      if (prev) prev.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: 'smooth' }));

      viewport.addEventListener('scroll', () => this.updateNav(viewport, state), { passive: true });
    }

    updateNav(viewport, state) {
      const prev = state.nav.querySelector('[data-logos-prev]');
      const next = state.nav.querySelector('[data-logos-next]');
      const max = state.track.scrollWidth - viewport.clientWidth;

      if (prev) prev.disabled = viewport.scrollLeft <= 1;
      if (next) next.disabled = viewport.scrollLeft >= max - 1;
    }

    /* ------------------------------------------------------------- helpers */

    gapOf(track) {
      const gap = window.getComputedStyle(track).columnGap;
      const value = parseFloat(gap);
      return isNaN(value) ? 0 : value;
    }

    onResize() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.instances.forEach((_, viewport) => this.layout(viewport));
      });
    }

    /* --------------------------------------------------- editor lifecycle */

    onBlockSelect(event) {
      const viewport = event.target.closest(this.selector);
      if (!viewport) return;

      // Freeze the marquee so the merchant can see the block they picked.
      viewport.classList.add('is-paused');
      event.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }

    onBlockDeselect(event) {
      const viewport = event.target.closest(this.selector);
      if (viewport) viewport.classList.remove('is-paused');
    }

    destroy(root) {
      const scope = root || document;
      scope.querySelectorAll(this.selector).forEach((viewport) => {
        const state = this.instances.get(viewport);
        if (!state) return;
        this.removeClone(state);
        if (this.resizeObserver) this.resizeObserver.unobserve(viewport);
        this.instances.delete(viewport);
      });
    }
  }

  /* ------------------------------------------------------------------------
     Accordion  (Section 06)

     `is-open` on the item is the single source of truth; `aria-expanded` on the
     trigger is kept in lockstep. CSS animates `grid-template-rows: 0fr -> 1fr`,
     so nothing here measures scrollHeight — a measured height desyncs when a
     web font swaps in, when the viewport resizes, or when a Theme Editor block
     changes the answer's length.

     Listeners are delegated to the accordion root, so `destroy()` detaches
     exactly one handler per instance and never leaks on section reload.
  ------------------------------------------------------------------------ */
  class Accordion {
    constructor() {
      this.selector = '[data-about-accordion]';
      this.instances = new Map();
    }

    init(root) {
      const scope = root || document;
      scope.querySelectorAll(this.selector).forEach((el) => this.setup(el));
    }

    setup(accordion) {
      if (this.instances.has(accordion)) return;

      const onClick = (event) => {
        const trigger = event.target.closest('[data-faq-trigger]');
        if (!trigger || !accordion.contains(trigger)) return;
        this.toggle(accordion, trigger.closest('[data-faq-item]'));
      };

      const onKeydown = (event) => {
        const trigger = event.target.closest('[data-faq-trigger]');
        if (!trigger || !accordion.contains(trigger)) return;

        const triggers = this.triggersOf(accordion);
        const i = triggers.indexOf(trigger);
        let next = -1;

        if (event.key === 'ArrowDown') next = (i + 1) % triggers.length;
        else if (event.key === 'ArrowUp') next = (i - 1 + triggers.length) % triggers.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = triggers.length - 1;
        else return;

        // Only now do we own the key — otherwise Tab/Space/Enter stay native.
        event.preventDefault();
        triggers[next].focus();
      };

      accordion.addEventListener('click', onClick);
      accordion.addEventListener('keydown', onKeydown);

      this.instances.set(accordion, {
        onClick: onClick,
        onKeydown: onKeydown,
        allowMultiple: accordion.dataset.allowMultiple === 'true'
      });
    }

    triggersOf(accordion) {
      return Array.prototype.slice.call(accordion.querySelectorAll('[data-faq-trigger]'));
    }

    setState(item, open) {
      if (!item) return;
      const trigger = item.querySelector('[data-faq-trigger]');
      item.classList.toggle('is-open', open);
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    toggle(accordion, item) {
      if (!item) return;
      const state = this.instances.get(accordion);
      const willOpen = !item.classList.contains('is-open');

      if (willOpen && state && !state.allowMultiple) {
        accordion.querySelectorAll('[data-faq-item].is-open').forEach((other) => {
          if (other !== item) this.setState(other, false);
        });
      }

      this.setState(item, willOpen);
    }

    open(accordion, item) {
      if (!item || item.classList.contains('is-open')) return;
      this.toggle(accordion, item);
    }

    destroy(root) {
      const scope = root || document;
      scope.querySelectorAll(this.selector).forEach((el) => {
        const state = this.instances.get(el);
        if (!state) return;
        el.removeEventListener('click', state.onClick);
        el.removeEventListener('keydown', state.onKeydown);
        this.instances.delete(el);
      });
    }

    /* Theme Editor: selecting a FAQ block reveals its answer. */
    onBlockSelect(event) {
      const item = event.target.closest
        ? event.target.closest('[data-faq-item]')
        : null;
      if (!item) return;
      const accordion = item.closest('[data-about-accordion]');
      if (accordion) this.open(accordion, item);
    }
  }

  /* ------------------------------------------------------------------------
     Module registry
  ------------------------------------------------------------------------ */
  const AboutTheme = {
    modules: new Map(),

    register(name, mod) {
      if (this.modules.has(name)) return this.modules.get(name);
      this.modules.set(name, mod);
      if (typeof mod.init === 'function') mod.init(document);
      return mod;
    },

    get(name) {
      return this.modules.get(name);
    },

    initAll(root) {
      this.modules.forEach((mod) => mod.init && mod.init(root));
    },

    destroyAll(root) {
      this.modules.forEach((mod) => mod.destroy && mod.destroy(root));
    },

    dispatch(hook, event) {
      this.modules.forEach((mod) => mod[hook] && mod[hook](event));
    }
  };

  window.AboutTheme = AboutTheme;

  /* ------------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------------ */
  const boot = () => {
    AboutTheme.register('animations', new AnimationEngine());
    AboutTheme.register('logoSlider', new LogoSlider());
    AboutTheme.register('accordion', new Accordion());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme Editor lifecycle — re-scan only the section that changed. */
  document.addEventListener('shopify:section:load', (e) => AboutTheme.initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => AboutTheme.destroyAll(e.target));
  document.addEventListener('shopify:block:select', (e) => AboutTheme.dispatch('onBlockSelect', e));
  document.addEventListener('shopify:block:deselect', (e) => AboutTheme.dispatch('onBlockDeselect', e));
})();
